import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft } from 'lucide-react'
import { fetchContent, fill, text } from '../api/content'
import {
  fetchReviewList,
  isStudioReview,
  NotHereError,
  reviewKey,
  reviewPath,
  whenLabel,
  type ReviewSource,
} from '../api/conversations'

/**
 * The list half of the supervisory view (D-626, D-629): every conversation
 * one account is part of, read from outside it.
 *
 * **It says on the screen what it returns and that opening one is
 * recorded.** For a staff account that is everything, the conversations she
 * holds as a parent included - a teacher who is also a parent has no private
 * conversation with the studio, and this is where she would find that out if
 * the compose screen had not already told her. For a dancer it is the read
 * her guardian agreed to on her behalf.
 *
 * Not mixed into the reader's own list: replying as herself in her
 * daughter's conversation is the easy mistake that separation prevents.
 */
export default function ReviewConversationsView({ source }: { source: ReviewSource }) {
  const {
    staffId = '',
    guardianId = '',
    dancerId = '',
  } = useParams<{ staffId?: string; guardianId?: string; dancerId?: string }>()

  const personId = source === 'staff' ? staffId : source === 'guardians' ? guardianId : dancerId
  const studio = isStudioReview(source)

  const { data: content } = useQuery({ queryKey: ['content'], queryFn: fetchContent })

  const list = useQuery({
    queryKey: reviewKey(source, personId),
    queryFn: () => fetchReviewList(source, personId),
    enabled: personId !== '',
  })

  const now = new Date()

  // Named once the name is here: while loading it says so, and a refusal
  // gets a neutral heading rather than a sentence with a hole in it.
  const heading =
    studio || list.isError
      ? text(content, 'messages.review.heading', 'Conversations')
      : list.data
        ? fill(content, 'portal.conversations.heading', { firstName: list.data.firstName }) ||
          `${list.data.firstName}'s conversations`
        : text(content, 'messages.loading', 'Loading…')

  // Back to where she came from, named honestly: the staff page, the families
  // page, or her own account.
  const back = source === 'staff' ? '/admin/staff' : source === 'guardians' ? '/admin/people' : '/account'
  const backLabel =
    source === 'staff'
      ? text(content, 'messages.review.backToStaff', 'Back to staff')
      : source === 'guardians'
        ? text(content, 'messages.review.backToPeople', 'Back to families')
        : text(content, 'portal.conversations.back', 'Back to your account')

  return (
    <div className="page-shell px-5 py-8">
      <div className="flex flex-col gap-3">
        <Link to={back} className="inline-flex min-h-[44px] items-center gap-2 text-[13px] font-semibold text-ink-2">
          <ArrowLeft size={16} aria-hidden="true" />
          {backLabel}
        </Link>

        <div className="min-w-0">
          <h1 className="font-display text-2xl wrap-anywhere">{heading}</h1>
          {list.data && studio && <p className="text-[13px] text-ink-2">{list.data.name}</p>}
        </div>

        <p className="text-[12.5px] leading-relaxed text-ink-2">
          {source === 'staff'
            ? text(
                content,
                'messages.review.intro',
                'Every conversation this account is part of, including any held as a parent. Opening one is recorded.',
              )
            : source === 'guardians'
              ? // Her own sentence: "including any held as a parent" is what a
                // staff account needs told and reads as nonsense to a parent.
                text(
                  content,
                  'messages.review.intro.guardian',
                  'Every conversation this account is part of. Opening one is recorded.',
                )
              : text(
                  content,
                  'portal.conversations.intro',
                  'Read-only. To say something to her teacher, use your own conversation with them.',
                )}
        </p>

        {list.isPending && (
          <p className="text-[13px] text-ink-2" role="status">
            {text(content, 'messages.loading', 'Loading…')}
          </p>
        )}

        {list.isError && (
          <div className="rounded-2xl border border-line bg-surface px-5 py-6" role="alert">
            <p className="text-[13px] leading-relaxed text-ink-2">
              {list.error instanceof NotHereError
                ? text(content, 'messages.review.notHere', 'Those conversations are not here.')
                : studio
                  ? text(content, 'messages.review.failed', 'Those conversations could not be loaded. Try again in a moment.')
                  : text(content, 'portal.conversations.failed', 'Her conversations could not be loaded. Try again in a moment.')}
            </p>
          </div>
        )}

        {list.data && list.data.conversations.length === 0 && (
          <p className="text-[13px] text-ink-2">
            {studio
              ? text(content, 'messages.review.none', 'This account is in no conversations.')
              : text(content, 'portal.conversations.none', 'No conversations yet.')}
          </p>
        )}

        {list.data && list.data.conversations.length > 0 && (
          <ol className="flex flex-col divide-y divide-line rounded-2xl border border-line bg-surface">
            {list.data.conversations.map((row) => (
              <li key={row.id}>
                <Link
                  to={`${reviewPath(source, personId)}/conversations/${row.id}`}
                  className="flex min-h-[44px] flex-col gap-0.5 px-4 py-3"
                >
                  <span className="flex items-baseline justify-between gap-3">
                    <span className="font-semibold wrap-anywhere">{row.title}</span>
                    {row.lastAt && <span className="shrink-0 text-[11px] text-ink-3">{whenLabel(row.lastAt, now)}</span>}
                  </span>
                  {row.lastLine && <span className="line-clamp-1 text-[12.5px] text-ink-2">{row.lastLine}</span>}
                </Link>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  )
}
